import type { Messages } from "@/i18n/locales/en";
import { legalMessagesEs } from "@/i18n/locales/legalEsData";
import { pagesMessagesEs } from "@/i18n/locales/pagesEsData";

export const esMessages = {
  meta: {
    description:
      "WasOK — escanea productos, guarda valoraciones y notas, recuerda lo que mereció la pena comprar.",
    title: "WasOK — Recuerda lo que mereció la pena comprar",
  },
  header: {
    navFeatures: "Funciones",
    navHowItWorks: "Cómo funciona",
    navPrivacy: "Privacidad",
    navUseCases: "Casos de uso",
    download: "Descargar",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    languageAria: "Idioma",
  },
  hero: {
    badge: "📱 Ya disponible en Android",
    title: "Recuerda lo que mereció la pena comprar.",
    subtitle:
      "Escanea productos, guarda valoraciones y notas y recuerda al instante si un producto era realmente bueno.",
    googlePlay: "Descargar en Google Play",
    watchDemo: "Ver demo",
    privacyLine: "Todos los datos permanecen en tu dispositivo.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Escanea para recordar",
    snackCard: "¡Delicioso!",
    coffeeCard: "Demasiado amargo",
    scrollHint: "Desplázate para explorar",
  },
  problems: {
    heading: "¿Te suena?",
    subheading:
      "Todos hemos estado ahí — mirando productos e intentando recordar si nos gustaron la última vez.",
    notedAgo: "✓ Lo anotaste hace 3 meses",
    items: [
      {
        question: "¿Qué chocolate me gustó de verdad?",
        note: "Dulzor perfecto",
      },
      {
        question: "¿Por qué dejé de comprar este champú?",
        note: "Me resecó el pelo",
      },
      {
        question: "¿Este café fue bueno o decepcionó?",
        note: "Demasiado amargo",
      },
    ],
  },
  howItWorks: {
    heading: "Cómo funciona",
    subheading:
      "Cuatro pasos sencillos para no olvidar tu opinión sobre los productos.",
    stepPrefix: "Paso",
    steps: [
      {
        title: "Escanea el código de barras de cualquier producto",
        description:
          "Rápido y fácil con la cámara. Funciona con cualquier código.",
      },
      {
        title: "Guarda valoraciones, comentarios y fotos",
        description:
          "Valora productos, añade notas, fotos, precios y lugares.",
      },
      {
        title: "Recuerda tu experiencia al instante",
        description:
          "Al escanear el mismo producto verás tu valoración y notas anteriores.",
      },
      {
        title: "Crea listas de compra inteligentes",
        description:
          "Haz listas con productos en los que ya confías.",
      },
    ],
  },
  privacy: {
    heading: "Tu historial de compras sigue siendo tuyo.",
    body: "WasOK te ayuda a recordar productos que has probado manteniendo tus valoraciones, notas, fotos y listas de la compra almacenadas localmente en tu dispositivo.",
    trustBanner:
      "💚 Pensado en tu historial de compras, no en recopilar tus datos.",
    bullets: [
      "No necesitas cuenta",
      "No hace falta sincronización en la nube",
      "Almacenamiento local prioritario",
      "Copias de seguridad bajo tu control",
      "Búsqueda opcional por código de barras para información del producto.",
      "Con publicidad mediante Google AdMob",
    ],
  },
  features: {
    heading: "Todo lo que necesitas",
    subheading:
      "Funciones potentes para llevar un seguimiento simple y privado.",
    items: [
      {
        title: "Escáner de códigos",
        description: "Escaneo rápido y preciso para cualquier producto.",
      },
      {
        title: "Valoraciones",
        description: "Valora de 1 a 5 estrellas para recordar la calidad.",
      },
      {
        title: "Fotos de producto",
        description: "Adjunta fotos del envase o detalles.",
      },
      {
        title: "Categorías",
        description: "Organiza productos en categorías propias.",
      },
      {
        title: "Tienda",
        description: "Recuerda dónde compraste cada producto.",
      },
      {
        title: "Precios",
        description: "Sigue precios para encontrar mejores ofertas.",
      },
      {
        title: "Listas de compra",
        description: "Crea listas con tus productos favoritos.",
      },
      {
        title: "Sin conexión",
        description: "Funciona perfectamente sin internet.",
      },
      {
        title: "Almacenamiento local",
        description: "Tus datos, seguros en tu dispositivo.",
      },
    ],
  },
  useCases: {
    heading: "Para cada pasillo",
    subheading:
      "Comida, belleza o hogar — WasOK te ayuda a recordar.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks y dulces",
        description: "Recuerda qué golosinas estaban realmente buenas.",
      },
      {
        emoji: "☕",
        title: "Café y bebidas",
        description: "Marca los cafés que merecen repetirse.",
      },
      {
        emoji: "💄",
        title: "Cosmética y cuidado",
        description: "Notas sobre reacciones en la piel.",
      },
      {
        emoji: "🧹",
        title: "Artículos del hogar",
        description: "Evita recomprar lo que falló.",
      },
    ],
  },
  screenshots: {
    heading: "Hermoso e intuitivo",
    subheading:
      "Una interfaz pensada para que llevar el control sea un placer.",
    items: [
      { title: "Escáner" },
      { title: "Detalle del producto" },
      { title: "Valoración" },
      { title: "Búsqueda e historial" },
      { title: "Lista de compra" },
    ],
  },
  cta: {
    heading: "Memoria de compras para la vida real.",
    body: "Deja de olvidar qué productos merecían la pena. Compra más inteligente hoy.",
    googlePlay: "Descargar en Google Play",
    scanQr: "Escanea para descargar",
    tagline: "Privado · Sin conexión · Simple",
  },
  footer: {
    tagline: "Recuerda lo que mereció la pena comprar.",
    linksHeading: "Enlaces",
    privacyPolicy: "Política de privacidad",
    terms: "Términos del servicio",
    contact: "Contacto",
    linkFaq: "Preguntas frecuentes",
    linkLicenses: "Licencias de código abierto",
    linkAdvertising: "Publicidad y monetización",
    downloadHeading: "Descargar",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Todos los derechos reservados. Hecho con 💚 para comprar mejor.",
  },
  legal: legalMessagesEs,
  faq: pagesMessagesEs.faq,
  support: pagesMessagesEs.support,
  advertising: pagesMessagesEs.advertising,
} satisfies Messages;
